import styles from './NavBar.module.scss';

import cx from 'classnames';
import { LogoLink } from 'components/LogoLink/LogoLink';
import { QA_TEST_PREFIX } from 'components/NavTitle/NavTitle';
import { redesignV2RoutePath } from 'constants/redesignV2';
import { useKeyPress } from 'hooks/useKeyPress';
import { useResizeObserver } from 'hooks/useResizeObserver';
import { useBreakpoints } from 'hooks/useBreakpoints';
import React, { forwardRef, useEffect, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { useHistory, useLocation } from 'react-router-dom';
import { isBrowser } from 'utils/UserAgent';
import type { LinkVariantAndIcon, NavBarInteraction, INavBarProps } from 'components/NavBar/navBarTypes';
import { useNavBarUtilItems } from 'components/NavBar/useNavBarUtilItems';
import { useNavBarContext } from '../NavBarContext/NavBarContext';
import { NavBarDesktop } from './NavBarDesktop/NavBarDesktop';
import { NavBarMobile } from './NavBarMobile/NavBarMobile';
import { NavBarTablet } from './NavBarTablet/NavBarTablet';
import { useUiController } from './UseUiController';
import { useCMSNavigation } from './useCMSNavigation';
import { NAV_BAR_V2_TEST_ID } from './NavBar.const';

export const logo = (
  <div className={styles.navBarLogo}>
    <LogoLink />
  </div>
);

export const NavBar = forwardRef<HTMLElement, INavBarProps>(
  ({ mainContentRef, className, ...props }, ref) => {
    const { mainMenu, utilMenu } = useCMSNavigation();
    const menuItems = mainMenu?.items || [];
    const utilItems = (utilMenu?.items || []) as LinkVariantAndIcon[];
    const { browser } = useBreakpoints();
    const isMobile = browser.is.mobile;
    const isTablet = browser.is.tablet;
    const isDesktopOrLarger = browser.is.desktop;

    const expandInteraction: NavBarInteraction = mainMenu?.interactionOnHover ? 'hover' : 'click';

    const { categorizeUtilItems } = useNavBarUtilItems();

    const { buttonUtilItems, textUtilItems, materialIconUtilItems, customIconUtilItems } =
      categorizeUtilItems(utilItems);

    const wrapperGroupRef = useRef<HTMLDivElement>(null);
    const canvasHeightCheckRef = useRef<HTMLDivElement>(null);

    const uiController = useUiController();
    const { isDrawerOpen, closeDrawer } = uiController;

    const { hasSubNavBar, isNavBarTransparent } = useNavBarContext();

    // If navigation is changed and drawer is open, close it
    const history = useHistory();
    useEffect(() => {
      const unregisterCallback = history.listen(closeDrawer);
      return () => {
        unregisterCallback();
      };
    }, []);

    useEffect(() => closeDrawer(), [isMobile, isDesktopOrLarger]);

    useKeyPress(['Escape'], isDrawerOpen ? closeDrawer : undefined);

    // Observe header height to adjust padding top of main container and set --header-height CSS variable
    useResizeObserver({
      targetRef: wrapperGroupRef,
      callback: ({ height }) => {
        if (mainContentRef?.current) mainContentRef.current.style.paddingTop = `${height}px`;
        document.body.style.setProperty('--header-height', `${height}px`);
      },
    });

    // Observe canvas height to set drawer height
    useResizeObserver({
      targetRef: canvasHeightCheckRef,
      callback: ({ height }) => {
        if (wrapperGroupRef.current)
          wrapperGroupRef.current.style.setProperty('--canvas-height', `${height}px`);
      },
    });

    useEffect(() => {
      return () => {
        if (mainContentRef?.current) mainContentRef.current.style.paddingTop = '';
      };
    }, []);

    // TODO: Redesign release plan
    // - Make default font size 1rem
    // - Show outline style with focus-visible
    useEffect(() => {
      if (!isBrowser) return;

      document.documentElement.classList.add(styles.redesignHtmlStyleOverride);

      return () => document.documentElement.classList.remove(styles.redesignHtmlStyleOverride);
    }, []);

    useEffect(() => {
      if (isDrawerOpen) {
        document.body.classList.add('nav-drawer--open');
      }

      return () => {
        document.body.classList.remove('nav-drawer--open');
      };
    }, [isDrawerOpen]);

    /**
     * Prevents the overlay from triggering early if the cursor is already on top
     * of the overlay when triggering the expand. Looks at the element that was
     * being hovered prior to hovering on the overlay. If the element is within
     * the navbar hierarchy or is from the Window event, this triggers the closing
     * of the nav drawer.
     *
     * @param {React.MouseEvent} event
     * @returns {void}
     */
    const handleOverlayHoverInteraction = (event: React.MouseEvent): void => {
      if (expandInteraction !== 'hover') return;
      const lastHoverElement: EventTarget | null = event.relatedTarget;
      if (lastHoverElement === window) {
        closeDrawer();
      } else if (lastHoverElement instanceof HTMLElement) {
        const lastElementWithinNavBar = !!lastHoverElement.closest(`.${styles.navBar}`);
        if (lastElementWithinNavBar) closeDrawer();
      }
    };
    const { pathname } = useLocation();
    const isCompact = pathname === redesignV2RoutePath.CART;
    const purchasePath = `/${pathname.split('/')[1]}`;
    const isPurchase = purchasePath === redesignV2RoutePath.PURCHASE;
    const isVisible = isCompact || isPurchase;
    return (
      <FocusLock disabled={!isDrawerOpen}>
        <header
          data-testid={NAV_BAR_V2_TEST_ID.navBar}
          ref={ref}
          className={cx(
            styles.navBar,
            hasSubNavBar && styles.navBarHide,
            isNavBarTransparent && styles.isNavBarTransparent,
            className,
          )}
          {...props}
        >
          <div ref={wrapperGroupRef} className={styles.navBarWrapperGroup}>
            <div
              data-testid={QA_TEST_PREFIX}
              className={styles.navBarWrapper}
              aria-modal={isDrawerOpen || undefined}
              {...props}
            >
              <button
                className={cx(styles.navBarDrawerOverlay, isDrawerOpen && styles.navBarDrawerOverlayShow)}
                aria-hidden="true"
                tabIndex={-1}
                onClick={expandInteraction === 'click' ? closeDrawer : undefined}
                onMouseEnter={handleOverlayHoverInteraction}
              />
              {isMobile && (
                <NavBarMobile
                  {...{
                    ...uiController,
                    buttonUtilItems,
                    customIconUtilItems,
                    menuItems,
                    textUtilItems,
                    materialIconUtilItems,
                  }}
                  isVisible={isVisible}
                />
              )}
              {isTablet && (
                <NavBarTablet
                  {...{
                    ...uiController,
                    buttonUtilItems,
                    customIconUtilItems,
                    menuItems,
                    textUtilItems,
                    materialIconUtilItems,
                  }}
                  isVisible={isVisible}
                />
              )}
              {isDesktopOrLarger && (
                <NavBarDesktop
                  {...{
                    ...uiController,
                    buttonUtilItems,
                    customIconUtilItems,
                    menuItems,
                    textUtilItems,
                    materialIconUtilItems,
                    expandInteraction,
                  }}
                  isVisible={isVisible}
                />
              )}
            </div>
          </div>
          <div className={styles.navBarCanvasHeightCheck} ref={canvasHeightCheckRef} />
        </header>
      </FocusLock>
    );
  },
);
